import type { EChartsCoreOption } from 'echarts/core';
import type {
  AlertControlRow,
  AlertSeverityRow,
  CostByProjectRow,
  HourlyBucket,
  RiskScoreBucket
} from '../../data-access/logs.api';
import { ALERTS_TAB_CHART_HEIGHT, USAGE_ALERT_THRESHOLD } from '../constants';
import { formatCostByProjectName, formatLogLabel } from '../formatters';

const SEVERITY_ORDER = ['Critical', 'High', 'Medium', 'Low'];

const SEVERITY_COLORS: Record<(typeof SEVERITY_ORDER)[number], string> = {
  Critical: '#dc2626',
  High: '#ea580c',
  Medium: '#ca8a04',
  Low: '#64748b'
};

/** Distinct from API/cost blues; tuned for white cards and overlapping lines. */
const RISK_SERIES = [
  { key: 'bias' as const, name: 'Bias', color: '#8b5cf6' },
  { key: 'fairness' as const, name: 'Fairness', color: '#14b8a6' },
  { key: 'prompt_injection' as const, name: 'Prompt Injection', color: '#f59e0b' },
  { key: 'pii_privacy' as const, name: 'PII / Privacy', color: '#f43f5e' }
] as const;

/** Light bar track behind columns (subtle, not dominant). */
const BAR_TRACK_BG = '#f4f6f8';

const axisNameStyle = { color: '#6b7280', fontSize: 11 };
const axisLabelStyle = { color: '#4b5563', fontSize: 11 };
const axisLineStyle = { lineStyle: { color: '#d1d5db' } };
const splitLineStyle = { lineStyle: { color: '#e5e7eb' } };

/** Y name vertical & centered left; X name horizontal & centered below ticks. */
const chartValueYAxis = (name: string, options?: { max?: number; minInterval?: number; decimalTicks?: boolean }) => ({
  type: 'value' as const,
  name,
  min: 0,
  ...(options?.max != null ? { max: options.max } : {}),
  ...(options?.minInterval != null ? { minInterval: options.minInterval } : {}),
  nameLocation: 'middle' as const,
  nameRotate: 90,
  nameGap: 42,
  nameTextStyle: axisNameStyle,
  axisLabel: options?.decimalTicks
    ? {
        ...axisLabelStyle,
        formatter: (value: number) => (Number.isInteger(value) ? `${value}.0` : String(value))
      }
    : axisLabelStyle,
  ...axisLineStyle,
  ...splitLineStyle
});

function truncateAxisLabel(value: string, maxLen = 22): string {
  if (value.length <= maxLen) return value;
  return `${value.slice(0, maxLen - 1)}…`;
}

const chartCategoryXAxis = (
  name: string,
  data: string[],
  options?: {
    boundaryGap?: boolean;
    rotate?: number;
    /** Gap between axis line and axis title (below tick labels). */
    bottomNameGap?: number;
    labelMargin?: number;
    truncateLabels?: boolean;
  }
) => {
  const rotated = options?.rotate != null;
  const axisLabel = rotated
    ? {
        rotate: options.rotate,
        fontSize: 10,
        color: '#4b5563',
        margin: options.labelMargin ?? 18,
        hideOverlap: true,
        ...(options.truncateLabels ? { formatter: (value: string) => truncateAxisLabel(value) } : {})
      }
    : { ...axisLabelStyle, margin: 10 };

  return {
    type: 'category' as const,
    name,
    data,
    ...(options?.boundaryGap === false ? { boundaryGap: false as const } : {}),
    nameLocation: 'middle' as const,
    nameGap: options?.bottomNameGap ?? (rotated ? 94 : 44),
    nameTextStyle: axisNameStyle,
    axisLabel,
    ...axisLineStyle
  };
};

/** Cyan/teal time-series palette (distinct from risk violets and cost blues). */
const API_SERIES = {
  accent: '#0891b2',
  light: '#22d3ee',
  deep: '#155e75',
  glow: 'rgba(8, 145, 178, 0.3)'
} as const;

const API_CALLS_LINE_GRADIENT = {
  type: 'linear' as const,
  x: 0,
  y: 0,
  x2: 1,
  y2: 0,
  colorStops: [
    { offset: 0, color: API_SERIES.light },
    { offset: 0.5, color: API_SERIES.accent },
    { offset: 1, color: API_SERIES.deep }
  ]
};

const API_CALLS_AREA_GRADIENT = {
  type: 'linear' as const,
  x: 0,
  y: 0,
  x2: 0,
  y2: 1,
  colorStops: [
    { offset: 0, color: 'rgba(34, 211, 238, 0.38)' },
    { offset: 0.55, color: 'rgba(8, 145, 178, 0.14)' },
    { offset: 1, color: 'rgba(8, 145, 178, 0.02)' }
  ]
};

export function buildApiCallsByHourOption(rows: HourlyBucket[]): EChartsCoreOption | null {
  if (!rows.length) return null;
  const hours = rows.map(r => r.hour);
  const counts = rows.map(r => r.call_count);
  const latencies = rows.map(r => r.avg_latency_ms);
  const pointCount = hours.length;
  const denseAxis = pointCount > 14;
  const enableZoom = pointCount > 20;
  const zoomWindowPct = pointCount > 24 ? Math.min(72, pointCount) / pointCount : 1;
  const zoomStart = enableZoom ? Math.round((1 - zoomWindowPct) * 100) : 0;

  const dataMax = Math.max(...counts, 0);
  const yMax =
    dataMax === 0
      ? 10
      : dataMax >= USAGE_ALERT_THRESHOLD
      ? Math.ceil(dataMax * 1.1)
      : Math.max(Math.ceil(dataMax * 1.25), dataMax + 2);

  const showThreshold = USAGE_ALERT_THRESHOLD <= yMax;
  const showPoints = pointCount <= 40;
  const latencyMax = Math.max(...latencies, 0);
  const latencyYMax = latencyMax === 0 ? 100 : Math.ceil(latencyMax * 1.15);
  const gridTop = 48;
  const gridRight = 72;

  return {
    animationDuration: 900,
    animationEasing: 'cubicOut',
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        crossStyle: { color: '#94a3b8', width: 1 },
        lineStyle: { type: 'dashed', color: '#cbd5e1', width: 1 }
      },
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      padding: [12, 16],
      textStyle: { color: '#374151', fontSize: 12 },
      extraCssText: 'box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12); border-radius: 12px;',
      formatter: (params: unknown) => {
        const item = (Array.isArray(params) ? params[0] : params) as { dataIndex?: number };
        const idx = item?.dataIndex ?? 0;
        const prev = idx > 0 ? counts[idx - 1]! : null;
        const delta = prev != null ? counts[idx]! - prev : null;
        const deltaHtml =
          delta == null
            ? ''
            : [
                `<div style="display:flex;justify-content:space-between;gap:20px;margin:4px 0">`,
                `<span style="color:#6b7280">vs prior hour</span>`,
                `<span style="font-weight:600;color:${delta >= 0 ? '#059669' : '#dc2626'}">`,
                `${delta >= 0 ? '+' : ''}${delta.toLocaleString()}`,
                `</span></div>`
              ].join('');
        return [
          `<div style="font-weight:600;font-size:13px;color:#111827;margin-bottom:8px">${hours[idx]}</div>`,
          `<div style="display:flex;justify-content:space-between;gap:20px;margin:4px 0">`,
          `<span style="color:#6b7280">API calls</span>`,
          `<span style="font-weight:700;color:${API_SERIES.accent}">${counts[idx]!.toLocaleString()}</span></div>`,
          deltaHtml,
          `<div style="display:flex;justify-content:space-between;gap:20px;margin:4px 0">`,
          `<span style="color:#6b7280">Avg latency</span>`,
          `<span style="font-weight:600;color:#d97706">${Math.round(latencies[idx] ?? 0)} ms</span></div>`
        ].join('');
      }
    },
    legend: {
      top: 8,
      left: 12,
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 4,
      textStyle: { fontSize: 11, color: '#64748b' },
      data: showThreshold ? ['API calls', 'Avg latency', 'Usage alert threshold'] : ['API calls', 'Avg latency']
    },
    grid: {
      left: 56,
      right: gridRight,
      top: gridTop,
      bottom: enableZoom ? 92 : denseAxis ? 80 : 56,
      containLabel: true
    },
    graphic: [
      {
        type: 'text',
        right: 14,
        top: gridTop - 20,
        style: {
          text: 'Latency (ms)',
          fill: axisNameStyle.color,
          fontSize: axisNameStyle.fontSize,
          fontWeight: 500,
          textAlign: 'right'
        },
        z: 10
      }
    ],
    ...(enableZoom
      ? {
          dataZoom: [
            {
              type: 'inside',
              start: zoomStart,
              end: 100,
              zoomOnMouseWheel: true,
              moveOnMouseMove: true
            },
            {
              type: 'slider',
              start: zoomStart,
              end: 100,
              height: 22,
              bottom: 8,
              borderColor: '#e5e7eb',
              backgroundColor: '#f8fafc',
              fillerColor: 'rgba(8, 145, 178, 0.12)',
              handleStyle: { color: API_SERIES.accent, borderColor: API_SERIES.accent },
              dataBackground: {
                lineStyle: { color: '#67e8f9', width: 1 },
                areaStyle: { color: 'rgba(103, 232, 249, 0.28)' }
              },
              selectedDataBackground: {
                lineStyle: { color: API_SERIES.accent, width: 1.5 },
                areaStyle: { color: 'rgba(8, 145, 178, 0.22)' }
              }
            }
          ]
        }
      : {}),
    xAxis: chartCategoryXAxis('Hour', hours, {
      boundaryGap: false,
      ...(denseAxis ? { rotate: 35, bottomNameGap: 72, labelMargin: 12, truncateLabels: true } : {})
    }),
    yAxis: [
      {
        ...chartValueYAxis('API Calls', { max: yMax }),
        splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } }
      },
      {
        type: 'value' as const,
        name: '',
        show: true,
        position: 'right' as const,
        min: 0,
        max: latencyYMax,
        axisLabel: {
          ...axisLabelStyle,
          margin: 10,
          align: 'left' as const,
          hideOverlap: true
        },
        axisLine: axisLineStyle,
        splitLine: { show: false }
      }
    ],
    series: [
      {
        name: 'API calls',
        type: 'line',
        yAxisIndex: 0,
        smooth: 0.42,
        connectNulls: true,
        symbol: 'circle',
        symbolSize: showPoints ? 7 : 0,
        showSymbol: showPoints,
        ...(pointCount > 48 ? { sampling: 'lttb' as const } : {}),
        lineStyle: {
          width: 3,
          color: API_CALLS_LINE_GRADIENT,
          shadowColor: API_SERIES.glow,
          shadowBlur: 10,
          shadowOffsetY: 3
        },
        itemStyle: { color: API_SERIES.accent, borderColor: '#fff', borderWidth: 2 },
        areaStyle: { color: API_CALLS_AREA_GRADIENT },
        emphasis: {
          focus: 'series',
          scale: true,
          lineStyle: { width: 3.5 },
          itemStyle: {
            color: API_SERIES.light,
            borderColor: '#fff',
            borderWidth: 2,
            shadowBlur: 12,
            shadowColor: 'rgba(8, 145, 178, 0.45)'
          }
        },
        data: counts,
        z: 2,
        ...(showThreshold
          ? {
              markArea: {
                silent: true,
                itemStyle: { borderWidth: 0 },
                data: [
                  [{ yAxis: 0 }, { yAxis: USAGE_ALERT_THRESHOLD, itemStyle: { color: 'rgba(8, 145, 178, 0.04)' } }]
                ]
              },
              markLine: {
                symbol: 'none',
                silent: true,
                lineStyle: { type: 'dashed', color: '#f59e0b', width: 1.5 },
                label: {
                  show: true,
                  position: 'insideEndTop',
                  formatter: `Alert ${USAGE_ALERT_THRESHOLD}`,
                  color: '#b45309',
                  fontSize: 10,
                  fontWeight: 500
                },
                data: [{ yAxis: USAGE_ALERT_THRESHOLD, name: 'Usage alert threshold' }]
              }
            }
          : {})
      },
      {
        name: 'Avg latency',
        type: 'line',
        yAxisIndex: 1,
        smooth: 0.42,
        symbol: 'none',
        showSymbol: false,
        ...(pointCount > 48 ? { sampling: 'lttb' as const } : {}),
        lineStyle: {
          width: 2,
          type: 'dashed',
          color: '#d97706',
          opacity: 0.9
        },
        itemStyle: { color: '#d97706' },
        emphasis: { focus: 'series', lineStyle: { width: 2.5 } },
        data: latencies,
        z: 3
      }
    ]
  };
}

export function buildRiskScoresByHourOption(rows: RiskScoreBucket[]): EChartsCoreOption | null {
  if (!rows.length) return null;
  const hours = rows.map(r => r.hour);
  const threshold = rows[0]?.threshold ?? 70;
  const pointCount = hours.length;
  const denseAxis = pointCount > 14;
  const enableZoom = pointCount > 20;
  const zoomWindowPct = pointCount > 24 ? Math.min(72, pointCount) / pointCount : 1;
  const zoomStart = enableZoom ? Math.round((1 - zoomWindowPct) * 100) : 0;

  const zoneLabel = (text: string, color: string) => ({
    show: true,
    position: 'insideTopLeft' as const,
    formatter: text,
    color,
    fontSize: 10,
    fontWeight: 600,
    opacity: 0.9,
    padding: [2, 6],
    distance: 6
  });

  const riskZoneSeries = {
    name: 'Risk zones',
    type: 'line' as const,
    data: hours.map(() => 0),
    symbol: 'none',
    lineStyle: { opacity: 0 },
    itemStyle: { opacity: 0 },
    silent: true,
    tooltip: { show: false },
    z: 0,
    markArea: {
      silent: true,
      itemStyle: { borderWidth: 0 },
      data: [
        [
          { yAxis: 0 },
          {
            yAxis: 40,
            itemStyle: { color: 'rgba(34, 197, 94, 0.07)' },
            label: zoneLabel('Low risk', '#15803d')
          }
        ],
        [
          { yAxis: 40 },
          {
            yAxis: threshold,
            itemStyle: { color: 'rgba(251, 191, 36, 0.06)' },
            label: zoneLabel('Medium', '#b45309')
          }
        ],
        [
          { yAxis: threshold },
          {
            yAxis: 100,
            itemStyle: { color: 'rgba(251, 113, 133, 0.08)' },
            label: zoneLabel('High risk', '#be123c')
          }
        ]
      ]
    },
    markLine: {
      symbol: 'none',
      silent: true,
      lineStyle: { type: 'dashed', color: '#e11d48', width: 2, opacity: 0.75 },
      label: {
        show: true,
        position: 'insideEndTop',
        formatter: `Threshold ${threshold}`,
        color: '#fff',
        backgroundColor: '#e11d48',
        padding: [4, 8],
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 600
      },
      data: [{ yAxis: threshold, name: 'High-risk threshold' }]
    }
  };

  const showRiskPoints = pointCount <= 36;
  const metricSeries = RISK_SERIES.map((s, index) => ({
    name: s.name,
    type: 'line' as const,
    z: index + 2,
    smooth: 0.42,
    symbol: 'circle',
    symbolSize: showRiskPoints ? 7 : 0,
    showSymbol: showRiskPoints,
    ...(pointCount > 48 ? { sampling: 'lttb' as const } : {}),
    lineStyle: {
      width: 2.5,
      color: s.color,
      shadowColor: `${s.color}44`,
      shadowBlur: 8,
      shadowOffsetY: 2
    },
    itemStyle: { color: '#ffffff', borderColor: s.color, borderWidth: 2.5 },
    emphasis: {
      focus: 'series' as const,
      scale: true,
      lineStyle: { width: 3.25, shadowBlur: 12 },
      itemStyle: { borderWidth: 3, shadowBlur: 8, shadowColor: `${s.color}55` }
    },
    data: rows.map(r => r[s.key])
  }));

  return {
    animationDuration: 800,
    animationEasing: 'cubicOut',
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        lineStyle: { color: '#a78bfa', width: 1.5, type: 'solid' },
        label: {
          show: true,
          backgroundColor: '#7c3aed',
          color: '#fff',
          fontSize: 10,
          padding: [4, 8],
          borderRadius: 4
        }
      },
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      padding: [12, 16],
      textStyle: { color: '#374151', fontSize: 12 },
      extraCssText: 'box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12); border-radius: 12px;',
      formatter: (params: unknown) => {
        const items = (Array.isArray(params) ? params : [params]) as Array<{ dataIndex?: number; seriesName?: string }>;
        const idx = items[0]?.dataIndex ?? 0;
        const maxAtHour = Math.max(...RISK_SERIES.map(s => rows[idx]![s.key]));
        const hourZone =
          maxAtHour >= threshold
            ? { label: 'High risk', bg: '#fef2f2', color: '#be123c' }
            : maxAtHour >= 40
            ? { label: 'Medium', bg: '#fffbeb', color: '#b45309' }
            : { label: 'Low risk', bg: '#f0fdf4', color: '#15803d' };
        const rows_html = RISK_SERIES.map(s => {
          const value = rows[idx]![s.key];
          const high = value >= threshold;
          const med = !high && value >= 40;
          return [
            `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin:5px 0;padding:4px 0;border-bottom:1px solid #f3f4f6">`,
            `<span style="display:inline-flex;align-items:center;gap:6px">`,
            `<span style="width:8px;height:8px;border-radius:50%;background:${s.color}"></span>`,
            `<span style="color:#374151;font-weight:500">${s.name}</span></span>`,
            `<span style="font-weight:700;font-size:13px;color:${
              high ? '#be123c' : med ? '#b45309' : '#111827'
            }">${value}`,
            high ? '<span style="font-size:10px;margin-left:2px">▲</span>' : '',
            `</span></div>`
          ].join('');
        }).join('');
        return [
          `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px">`,
          `<span style="font-weight:600;font-size:13px;color:#111827">${hours[idx]}</span>`,
          `<span style="font-size:10px;font-weight:600;padding:3px 8px;border-radius:999px;background:${hourZone.bg};color:${hourZone.color}">`,
          hourZone.label,
          `</span></div>`,
          rows_html
        ].join('');
      }
    },
    legend: {
      top: 8,
      left: 'center',
      type: 'scroll',
      icon: 'roundRect',
      itemWidth: 14,
      itemHeight: 3,
      itemGap: 18,
      textStyle: { fontSize: 11, color: '#475569', fontWeight: 500 },
      data: RISK_SERIES.map(s => s.name)
    },
    grid: {
      left: 56,
      right: 24,
      top: 44,
      bottom: enableZoom ? 92 : denseAxis ? 80 : 56,
      containLabel: true
    },
    ...(enableZoom
      ? {
          dataZoom: [
            { type: 'inside', start: zoomStart, end: 100 },
            {
              type: 'slider',
              start: zoomStart,
              end: 100,
              height: 22,
              bottom: 8,
              borderColor: '#e5e7eb',
              backgroundColor: '#fafafa',
              fillerColor: 'rgba(139, 92, 246, 0.12)',
              handleStyle: { color: '#8b5cf6', borderColor: '#8b5cf6' },
              dataBackground: {
                lineStyle: { color: '#c4b5fd', width: 1 },
                areaStyle: { color: 'rgba(196, 181, 253, 0.25)' }
              },
              selectedDataBackground: {
                lineStyle: { color: '#8b5cf6', width: 1.5 },
                areaStyle: { color: 'rgba(139, 92, 246, 0.18)' }
              }
            }
          ]
        }
      : {}),
    xAxis: chartCategoryXAxis('Hour', hours, {
      boundaryGap: false,
      ...(denseAxis ? { rotate: 35, bottomNameGap: 72, labelMargin: 12, truncateLabels: true } : {})
    }),
    yAxis: {
      ...chartValueYAxis('Risk Score', { max: 100, minInterval: 20 }),
      splitLine: { lineStyle: { color: '#eef2f7', type: 'dashed', width: 1 } },
      axisLine: { lineStyle: { color: '#e2e8f0' } }
    },
    series: [riskZoneSeries, ...metricSeries]
  };
}

/** Sequential blues: light (low cost) → dark (high cost). */
const COST_BLUE_LIGHT = { r: 219, g: 234, b: 254 }; // #dbeafe
const COST_BLUE_DARK = { r: 30, g: 64, b: 175 }; // #1e40af

function toHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map(c => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

/** Darker blue for higher cost; lighter as value decreases (rank fallback when values tie). */
function costBarColor(value: number, minVal: number, maxVal: number, rankIndex: number, count: number): string {
  const span = maxVal - minVal;
  const t = span > 1e-9 ? (value - minVal) / span : count <= 1 ? 1 : 1 - rankIndex / (count - 1);

  const r = COST_BLUE_LIGHT.r + (COST_BLUE_DARK.r - COST_BLUE_LIGHT.r) * t;
  const g = COST_BLUE_LIGHT.g + (COST_BLUE_DARK.g - COST_BLUE_LIGHT.g) * t;
  const b = COST_BLUE_LIGHT.b + (COST_BLUE_DARK.b - COST_BLUE_LIGHT.b) * t;
  return toHex(r, g, b);
}

function costBarShadow(hex: string): string {
  const normalized = hex.replace('#', '');
  const n = Number.parseInt(normalized, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, 0.32)`;
}

const COST_CHART_HEIGHT = { min: 360, max: 520, row: 40, padding: 88 } as const;

export function getCostByProjectChartHeight(rowCount: number): number {
  const rows = Math.max(rowCount, 4);
  return Math.min(
    COST_CHART_HEIGHT.max,
    Math.max(COST_CHART_HEIGHT.min, rows * COST_CHART_HEIGHT.row + COST_CHART_HEIGHT.padding)
  );
}

export function buildCostByProjectOption(rows: CostByProjectRow[]): EChartsCoreOption | null {
  if (!rows.length) return null;
  const sorted = [...rows].sort((a, b) => b.estimated_cost - a.estimated_cost);
  const labels = sorted.map(r => formatCostByProjectName(r.project_name));
  const costs = sorted.map(r => r.estimated_cost);
  const calls = sorted.map(r => r.call_count);
  const minCost = Math.min(...costs);
  const maxCost = Math.max(...costs, 0.01);
  const totalCost = costs.reduce((sum, v) => sum + v, 0);

  return {
    animationDuration: 700,
    animationEasing: 'cubicOut',
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
        shadowStyle: { color: 'rgba(99, 102, 241, 0.12)' }
      },
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      padding: [12, 16],
      textStyle: { color: '#374151', fontSize: 12 },
      extraCssText: 'box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12); border-radius: 12px;',
      formatter: (params: unknown) => {
        const item = (Array.isArray(params) ? params[0] : params) as { dataIndex?: number };
        const idx = item?.dataIndex ?? 0;
        const fill = costBarColor(costs[idx]!, minCost, maxCost, idx, costs.length);
        const share = totalCost > 0 ? ((costs[idx]! / totalCost) * 100).toFixed(1) : '0.0';
        const rank = idx + 1;
        return [
          `<div style="font-weight:600;font-size:13px;color:#111827;margin-bottom:6px">${labels[idx]}</div>`,
          `<div style="font-size:11px;color:#6b7280;margin-bottom:8px">Rank #${rank} by estimated cost</div>`,
          `<div style="display:flex;justify-content:space-between;gap:16px;margin:4px 0">`,
          `<span style="color:#6b7280">Estimated cost</span>`,
          `<span style="font-weight:700;color:${fill}">$${costs[idx]!.toFixed(2)}</span></div>`,
          `<div style="display:flex;justify-content:space-between;gap:16px;margin:4px 0">`,
          `<span style="color:#6b7280">API calls</span>`,
          `<span style="font-weight:600;color:#111827">${calls[idx]!.toLocaleString()}</span></div>`,
          `<div style="display:flex;justify-content:space-between;gap:16px;margin:4px 0">`,
          `<span style="color:#6b7280">Share of total spend</span>`,
          `<span style="font-weight:600;color:#111827">${share}%</span></div>`
        ].join('');
      }
    },
    grid: { left: 8, right: 72, top: 20, bottom: 36, containLabel: true },
    xAxis: {
      type: 'value',
      min: 0,
      max: Math.ceil(maxCost * 1.12 * 100) / 100,
      name: 'Estimated cost ($)',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { ...axisNameStyle, fontWeight: 500 },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#9ca3af',
        fontSize: 11,
        formatter: (value: number) => `$${value}`
      },
      splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } }
    },
    yAxis: {
      type: 'category',
      data: labels,
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#374151',
        fontSize: 12,
        fontWeight: 500,
        width: 128,
        overflow: 'truncate',
        formatter: (value: string) => truncateAxisLabel(value, 20)
      }
    },
    series: [
      {
        name: 'Estimated cost',
        type: 'bar',
        data: costs.map((value, index) => {
          const fill = costBarColor(value, minCost, maxCost, index, costs.length);
          return {
            value,
            itemStyle: {
              color: fill,
              borderRadius: [0, 10, 10, 0],
              shadowColor: costBarShadow(fill),
              shadowBlur: 6,
              shadowOffsetY: 2
            }
          };
        }),
        barMaxWidth: 24,
        barCategoryGap: '42%',
        showBackground: true,
        backgroundStyle: { color: BAR_TRACK_BG, borderRadius: [0, 10, 10, 0] },
        emphasis: {
          focus: 'series',
          itemStyle: {
            shadowBlur: 12,
            borderWidth: 0
          }
        },
        label: {
          show: true,
          position: 'right',
          distance: 10,
          color: '#374151',
          fontWeight: 600,
          fontSize: 11,
          formatter: ({ value }: { value: number }) => `$${Number(value).toFixed(2)}`
        }
      }
    ]
  };
}

const ALERT_BAR_LIGHT = { r: 254, g: 215, b: 170 };
const ALERT_BAR_DARK = { r: 194, g: 65, b: 12 };

export function getAlertsControlChartHeight(analysisTypeCount = 5): number {
  const rowHeight = 44;
  const chrome = 96;
  return Math.max(ALERTS_TAB_CHART_HEIGHT, chrome + analysisTypeCount * rowHeight);
}

const modernTooltipStyle = {
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  borderColor: '#e5e7eb',
  borderWidth: 1,
  padding: [12, 16] as [number, number],
  textStyle: { color: '#374151', fontSize: 12 },
  extraCssText: 'box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12); border-radius: 12px;'
};

function formatControlLabel(control: string): string {
  return formatLogLabel(control);
}

function alertBarColor(value: number, minVal: number, maxVal: number, rankIndex: number, count: number): string {
  const span = maxVal - minVal;
  const t = span > 1e-9 ? (value - minVal) / span : count <= 1 ? 1 : 1 - rankIndex / (count - 1);
  const r = ALERT_BAR_LIGHT.r + (ALERT_BAR_DARK.r - ALERT_BAR_LIGHT.r) * t;
  const g = ALERT_BAR_LIGHT.g + (ALERT_BAR_DARK.g - ALERT_BAR_LIGHT.g) * t;
  const b = ALERT_BAR_LIGHT.b + (ALERT_BAR_DARK.b - ALERT_BAR_LIGHT.b) * t;
  return toHex(r, g, b);
}

function severityBarGradient(hex: string) {
  return {
    type: 'linear' as const,
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: `${hex}f0` },
      { offset: 1, color: hex }
    ]
  };
}

export function buildAlertsSeverityOption(rows: AlertSeverityRow[]): EChartsCoreOption | null {
  if (!rows.length) return null;
  const ordered = SEVERITY_ORDER.map(
    label => rows.find(r => r.severity === label) ?? { severity: label, alert_count: 0 }
  );
  const counts = ordered.map(r => r.alert_count);
  const total = counts.reduce((sum, v) => sum + v, 0);
  const yMax = Math.max(...counts, 1);

  return {
    animationDuration: 700,
    animationEasing: 'cubicOut',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(220, 38, 38, 0.1)' } },
      ...modernTooltipStyle,
      formatter: (params: unknown) => {
        const item = (Array.isArray(params) ? params[0] : params) as { dataIndex?: number };
        const idx = item?.dataIndex ?? 0;
        const severity = ordered[idx]!.severity;
        const count = counts[idx]!;
        const color = SEVERITY_COLORS[severity as keyof typeof SEVERITY_COLORS] ?? '#64748b';
        const share = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
        return [
          `<div style="font-weight:600;font-size:13px;color:#111827;margin-bottom:8px">${severity}</div>`,
          `<div style="display:flex;justify-content:space-between;gap:16px;margin:4px 0">`,
          `<span style="color:#6b7280">Open events</span>`,
          `<span style="font-weight:700;color:${color}">${count.toLocaleString()}</span></div>`,
          `<div style="display:flex;justify-content:space-between;gap:16px;margin:4px 0">`,
          `<span style="color:#6b7280">Share of total</span>`,
          `<span style="font-weight:600;color:#111827">${share}%</span></div>`
        ].join('');
      }
    },
    grid: { left: 56, right: 24, top: 36, bottom: 56, containLabel: true },
    xAxis: chartCategoryXAxis(
      'Severity',
      ordered.map(r => r.severity),
      { boundaryGap: true }
    ),
    yAxis: {
      ...chartValueYAxis('Alert Count', { max: Math.ceil(yMax * 1.15), minInterval: 1 }),
      splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } }
    },
    series: [
      {
        name: 'Open events',
        type: 'bar',
        data: ordered.map(r => {
          const color = SEVERITY_COLORS[r.severity as keyof typeof SEVERITY_COLORS] ?? '#64748b';
          return {
            value: r.alert_count,
            itemStyle: {
              color: severityBarGradient(color),
              borderRadius: [8, 8, 0, 0],
              shadowColor: `${color}44`,
              shadowBlur: 6,
              shadowOffsetY: 2
            }
          };
        }),
        barMaxWidth: 52,
        barCategoryGap: '38%',
        showBackground: false,
        emphasis: {
          focus: 'series',
          itemStyle: { shadowBlur: 12 }
        },
        label: {
          show: true,
          position: 'top',
          distance: 6,
          color: '#475569',
          fontWeight: 600,
          fontSize: 11,
          formatter: ({ value }: { value: number }) => (value > 0 ? String(value) : '')
        }
      }
    ]
  };
}

const LLM_BREAKDOWN_SEVERITY_COLORS: Record<string, string> = {
  Critical: SEVERITY_COLORS.Critical,
  High: SEVERITY_COLORS.High,
  Medium: SEVERITY_COLORS.Medium,
  Low: SEVERITY_COLORS.Low,
  Unknown: '#94a3b8',
  Unspecified: '#cbd5e1'
};

export type LlmBreakdownStackedChartInput = {
  analysisTypes: Array<{ key: string; label: string }>;
  segments: Array<{
    severity: string;
    counts: number[];
    avgScores: (number | null)[];
  }>;
};

function llmBreakdownSeverityColor(severity: string): string {
  return LLM_BREAKDOWN_SEVERITY_COLORS[severity] ?? '#64748b';
}

export function buildLlmAnalysisBreakdownOption(chart: LlmBreakdownStackedChartInput | null): EChartsCoreOption | null {
  if (!chart?.analysisTypes.length || !chart.segments.length) return null;

  const { analysisTypes, segments } = chart;
  const typeLabels = analysisTypes.map(t => t.label);
  const typeTotals = analysisTypes.map((_, typeIdx) =>
    segments.reduce((sum, segment) => sum + (segment.counts[typeIdx] ?? 0), 0)
  );
  const maxTypeTotal = Math.max(...typeTotals, 1);
  const grandTotal = typeTotals.reduce((sum, value) => sum + value, 0);
  const lastSegmentIndex = segments.length - 1;

  return {
    animationDuration: 700,
    animationEasing: 'cubicOut',
    legend: {
      top: 4,
      right: 8,
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 12,
      textStyle: { color: '#64748b', fontSize: 11 },
      data: segments.map(segment => segment.severity)
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(234, 88, 12, 0.12)' } },
      ...modernTooltipStyle,
      formatter: (params: unknown) => {
        const items = (Array.isArray(params) ? params : [params]) as Array<{
          seriesName?: string;
          dataIndex?: number;
          value?: number;
          color?: string;
          data?: { avgScore?: number | null };
        }>;
        const typeIdx = items[0]?.dataIndex ?? 0;
        const typeLabel = typeLabels[typeIdx] ?? '';
        const typeTotal = typeTotals[typeIdx] ?? 0;
        const typeShare = grandTotal > 0 ? ((typeTotal / grandTotal) * 100).toFixed(1) : '0.0';

        const lines = [
          `<div style="font-weight:600;font-size:13px;color:#111827;margin-bottom:6px">${typeLabel}</div>`,
          `<div style="display:flex;justify-content:space-between;gap:16px;margin:4px 0 8px">`,
          `<span style="color:#6b7280">Total events</span>`,
          `<span style="font-weight:700;color:#111827">${typeTotal.toLocaleString()}</span></div>`,
          `<div style="display:flex;justify-content:space-between;gap:16px;margin:0 0 10px">`,
          `<span style="color:#6b7280">Share of all</span>`,
          `<span style="font-weight:600;color:#111827">${typeShare}%</span></div>`
        ];

        for (const item of items) {
          const count = Number(item.value ?? 0);
          if (count <= 0) continue;
          const severity = item.seriesName ?? '';
          const color = llmBreakdownSeverityColor(severity);
          const avgScore = item.data?.avgScore;
          const avgLabel = avgScore != null ? avgScore.toFixed(2) : '—';
          const segShare = typeTotal > 0 ? ((count / typeTotal) * 100).toFixed(1) : '0.0';
          lines.push(
            `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #f1f5f9">`,
            `<div style="font-weight:600;font-size:12px;color:${color};margin-bottom:4px">${severity}</div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px;margin:2px 0">`,
            `<span style="color:#6b7280">Events</span>`,
            `<span style="font-weight:600;color:#111827">${count.toLocaleString()}</span></div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px;margin:2px 0">`,
            `<span style="color:#6b7280">Of control</span>`,
            `<span style="font-weight:600;color:#111827">${segShare}%</span></div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px;margin:2px 0">`,
            `<span style="color:#6b7280">Avg score</span>`,
            `<span style="font-weight:600;color:#111827">${avgLabel}</span></div>`,
            `</div>`
          );
        }

        return lines.join('');
      }
    },
    grid: { left: 8, right: 56, top: 40, bottom: 28, containLabel: true },
    xAxis: {
      type: 'value',
      min: 0,
      max: Math.ceil(maxTypeTotal * 1.12),
      name: 'Result count',
      nameLocation: 'middle',
      nameGap: 28,
      nameTextStyle: { ...axisNameStyle, fontWeight: 500 },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#9ca3af', fontSize: 11 },
      splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } }
    },
    yAxis: {
      type: 'category',
      data: typeLabels,
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#374151',
        fontSize: 12,
        fontWeight: 500,
        width: 140,
        overflow: 'truncate',
        formatter: (value: string) => truncateAxisLabel(value, 22)
      }
    },
    series: segments.map((segment, segmentIndex) => {
      const color = llmBreakdownSeverityColor(segment.severity);
      const isLast = segmentIndex === lastSegmentIndex;
      return {
        name: segment.severity,
        type: 'bar' as const,
        stack: 'breakdown',
        data: segment.counts.map((value, typeIdx) => ({
          value,
          avgScore: segment.avgScores[typeIdx] ?? null
        })),
        barMaxWidth: 24,
        barCategoryGap: '36%',
        itemStyle: {
          color: severityBarGradient(color),
          shadowColor: `${color}33`,
          shadowBlur: 4,
          shadowOffsetY: 1
        },
        emphasis: { focus: 'series' as const, itemStyle: { shadowBlur: 10 } },
        label: {
          show: true,
          position: isLast ? ('right' as const) : ('inside' as const),
          distance: isLast ? 8 : 0,
          color: isLast ? '#475569' : '#1f2937',
          fontWeight: 600,
          fontSize: 11,
          formatter: (labelParams: { seriesIndex?: number; dataIndex?: number; value?: number }) => {
            if (isLast) {
              const idx = labelParams.dataIndex ?? 0;
              const total = typeTotals[idx] ?? 0;
              return total > 0 ? String(total) : '';
            }
            const value = Number(labelParams.value ?? 0);
            return value > 1 ? String(value) : '';
          }
        }
      };
    })
  };
}

export function buildAlertsControlOption(rows: AlertControlRow[]): EChartsCoreOption | null {
  if (!rows.length) return null;
  const sorted = [...rows].sort((a, b) => b.alert_count - a.alert_count);
  const labels = sorted.map(r => formatControlLabel(r.control));
  const counts = sorted.map(r => r.alert_count);
  const minCount = Math.min(...counts);
  const maxCount = Math.max(...counts, 1);
  const total = counts.reduce((sum, v) => sum + v, 0);

  return {
    animationDuration: 700,
    animationEasing: 'cubicOut',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(234, 88, 12, 0.12)' } },
      ...modernTooltipStyle,
      formatter: (params: unknown) => {
        const item = (Array.isArray(params) ? params[0] : params) as { dataIndex?: number };
        const idx = item?.dataIndex ?? 0;
        const fill = alertBarColor(counts[idx]!, minCount, maxCount, idx, counts.length);
        const share = total > 0 ? ((counts[idx]! / total) * 100).toFixed(1) : '0.0';
        return [
          `<div style="font-weight:600;font-size:13px;color:#111827;margin-bottom:6px">${labels[idx]}</div>`,
          `<div style="font-size:11px;color:#6b7280;margin-bottom:8px">Rank #${idx + 1} by event count</div>`,
          `<div style="display:flex;justify-content:space-between;gap:16px;margin:4px 0">`,
          `<span style="color:#6b7280">Events</span>`,
          `<span style="font-weight:700;color:${fill}">${counts[idx]!.toLocaleString()}</span></div>`,
          `<div style="display:flex;justify-content:space-between;gap:16px;margin:4px 0">`,
          `<span style="color:#6b7280">Share of total</span>`,
          `<span style="font-weight:600;color:#111827">${share}%</span></div>`
        ].join('');
      }
    },
    grid: { left: 8, right: 52, top: 16, bottom: 28, containLabel: true },
    xAxis: {
      type: 'value',
      min: 0,
      max: Math.ceil(maxCount * 1.12),
      name: 'Event count',
      nameLocation: 'middle',
      nameGap: 28,
      nameTextStyle: { ...axisNameStyle, fontWeight: 500 },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#9ca3af', fontSize: 11 },
      splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } }
    },
    yAxis: {
      type: 'category',
      data: labels,
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#374151',
        fontSize: 12,
        fontWeight: 500,
        width: 140,
        overflow: 'truncate',
        formatter: (value: string) => truncateAxisLabel(value, 22)
      }
    },
    series: [
      {
        name: 'Events',
        type: 'bar',
        data: counts.map((value, index) => {
          const fill = alertBarColor(value, minCount, maxCount, index, counts.length);
          return {
            value,
            itemStyle: {
              color: fill,
              borderRadius: [0, 8, 8, 0],
              shadowColor: costBarShadow(fill),
              shadowBlur: 6,
              shadowOffsetY: 2
            }
          };
        }),
        barMaxWidth: 22,
        barCategoryGap: '40%',
        showBackground: false,
        emphasis: { focus: 'series', itemStyle: { shadowBlur: 12 } },
        label: {
          show: true,
          position: 'right',
          distance: 8,
          color: '#475569',
          fontWeight: 600,
          fontSize: 11,
          formatter: ({ value }: { value: number }) => (Number(value) > 0 ? String(value) : '')
        }
      }
    ]
  };
}
